import React, { useEffect, useRef } from "react";

export function parseVideo(url) {
  if (!url || typeof url !== "string") return null;
  const u = url.trim();
  if (!u) return null;
  const yt = u.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return { type: "youtube", id: yt[1] };
  const vim = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vim) return { type: "vimeo", id: vim[1] };
  if (/^https?:\/\//.test(u) || u.startsWith("/api/")) return { type: "file", url: u };
  return null;
}

let ytApiPromise = null;
function loadYouTubeAPI() {
  if (typeof window !== "undefined" && window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (!ytApiPromise) {
    ytApiPromise = new Promise((resolve) => {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(tag);
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { if (prev) prev(); resolve(window.YT); };
    });
  }
  return ytApiPromise;
}

// Fond vidéo plein cadre avec segment optionnel (start/end en secondes).
// cover=true => couvre le viewport (hero plein écran) ; cover=false => remplit un conteneur 16:9.
// muted (contrôlé par le parent) : le bouton son est rendu par la page à côté des CTA.
export function MediaBackground({ videoUrl, start = 0, end = 0, cover = true, muted = true }) {
  const v = parseVideo(videoUrl);
  const mountRef = useRef(null);
  const videoElRef = useRef(null);
  const vimeoRef = useRef(null);
  const playerRef = useRef(null);
  const readyRef = useRef(false);
  const s = Math.max(0, Number(start) || 0);
  const e = Math.max(0, Number(end) || 0);

  const iframeCls = cover
    ? "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.78vh] min-w-full h-[56.25vw] min-h-full pointer-events-none"
    : "absolute inset-0 w-full h-full pointer-events-none";

  // YouTube via IFrame API (boucle propre sur le segment)
  useEffect(() => {
    if (!v || v.type !== "youtube" || !mountRef.current) return;
    let cancelled = false;
    readyRef.current = false;
    const holder = document.createElement("div");
    mountRef.current.appendChild(holder);
    loadYouTubeAPI().then((YT) => {
      if (cancelled) return;
      playerRef.current = new YT.Player(holder, {
        videoId: v.id,
        playerVars: {
          autoplay: 1, mute: 1, controls: 0, modestbranding: 1, rel: 0,
          playsinline: 1, showinfo: 0, iv_load_policy: 3, disablekb: 1, fs: 0,
          start: s || undefined, end: e || undefined,
          loop: 1, playlist: v.id,
        },
        events: {
          onReady: (ev) => {
            readyRef.current = true;
            if (muted) { ev.target.mute(); } else { ev.target.unMute(); ev.target.setVolume(100); }
            ev.target.playVideo();
            const f = ev.target.getIframe && ev.target.getIframe();
            if (f) f.className = iframeCls;
          },
          onStateChange: (ev) => {
            // ENDED (segment terminé ou vidéo finie) -> relance au point de départ
            if (ev.data === window.YT.PlayerState.ENDED) {
              ev.target.seekTo(s || 0);
              ev.target.playVideo();
            }
          },
        },
      });
    });
    return () => {
      cancelled = true;
      readyRef.current = false;
      try { if (playerRef.current && playerRef.current.destroy) playerRef.current.destroy(); } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v && v.id, s, e, cover]);

  // Applique l'état muet/son (contrôlé par le parent) au lecteur courant
  useEffect(() => {
    if (!v) return;
    if (v.type === "youtube" && playerRef.current && readyRef.current) {
      try {
        if (muted) { playerRef.current.mute(); }
        else { playerRef.current.unMute(); playerRef.current.setVolume(100); }
      } catch (_) {}
    } else if (v.type === "file" && videoElRef.current) {
      videoElRef.current.muted = muted;
      if (!muted) { try { videoElRef.current.play(); } catch (_) {} }
    } else if (v.type === "vimeo" && vimeoRef.current && vimeoRef.current.contentWindow) {
      try {
        vimeoRef.current.contentWindow.postMessage(
          JSON.stringify({ method: "setVolume", value: muted ? 0 : 1 }),
          "*"
        );
      } catch (_) {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [muted, v && v.id]);

  if (!v) return null;

  if (v.type === "youtube") {
    return (
      <>
        <div className="absolute inset-0 overflow-hidden">
          <div ref={mountRef} className="absolute inset-0" data-testid="media-video-embed" />
        </div>
        <div className="absolute inset-0 bg-black/35 pointer-events-none" />
      </>
    );
  }

  if (v.type === "vimeo") {
    const hash = s ? `#t=${s}s` : "";
    const src = `https://player.vimeo.com/video/${v.id}?autoplay=1&muted=1&loop=1&background=1${hash}`;
    return (
      <>
        <div className="absolute inset-0 overflow-hidden">
          <iframe ref={vimeoRef} className={iframeCls} src={src} title="Vidéo" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen data-testid="media-video-embed" />
        </div>
        <div className="absolute inset-0 bg-black/35 pointer-events-none" />
      </>
    );
  }

  // Fichier vidéo (mp4/webm...) : segment via events
  return (
    <>
      <video
        ref={videoElRef}
        className="absolute inset-0 w-full h-full object-cover"
        src={v.url}
        autoPlay
        muted
        loop={!e}
        playsInline
        data-testid="media-video-file"
        onLoadedMetadata={() => { if (s && videoElRef.current) videoElRef.current.currentTime = s; }}
        onTimeUpdate={() => {
          const vid = videoElRef.current;
          if (!vid) return;
          if (e && vid.currentTime >= e) { vid.currentTime = s || 0; vid.play(); }
        }}
      />
      <div className="absolute inset-0 bg-black/35 pointer-events-none" />
    </>
  );
}
